import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { LoginCallback } from '@okta/okta-react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  AccountTree,
  Refresh,
  Close,
  Logout,
  ExpandMore,
  FileDownload,
  Apps,
  AccountBalance,
  Security,
  CloudQueue,
  Assessment,
  FilterList,
  ContentCopy
} from '@mui/icons-material';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('DataGrid Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          <Typography variant="h6">Something went wrong with the data display</Typography>
          <Typography variant="body2">Please try refreshing the page or clearing filters.</Typography>
          <Button 
            onClick={() => this.setState({ hasError: false, error: null })} 
            sx={{ mt: 1 }}
            variant="outlined"
            size="small"
          >
            Try Again
          </Button>
        </Alert>
      );
    }

    return this.props.children;
  }
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://your-api-gateway-url/prod';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
  },
});

const drawerWidth = 320;

const Login = () => {
  const { oktaAuth } = useOktaAuth();
  
  useEffect(() => {
    oktaAuth.signInWithRedirect();
  }, [oktaAuth]);
  
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Redirecting to Okta...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we redirect you to sign in.
        </Typography>
      </Paper>
    </Container>
  );
};

const Dashboard = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [allAccounts, setAllAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [allOUs, setAllOUs] = useState([]);
  const [accountCounts, setAccountCounts] = useState({ total: 0, active: 0, suspended: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOU, setSelectedOU] = useState('');
  const [currentOU, setCurrentOU] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);

  const handleRowClick = async (account) => {
    setSelectedAccount(account);
    setDetailsPanelOpen(true);
    
    // Fetch approvers if not already loaded
    if (!account.approvers) {
      try {
        const response = await authenticatedFetch(`${API_ENDPOINT}/accounts/${account.id}/approvers`);
        const data = await response.json();
        if (data.approvers) {
          account.approvers = data.approvers;
          setSelectedAccount({...account});
        }
      } catch (err) {
        console.error('Error fetching approvers:', err);
      }
    }
  };

  const closeDetailsPanel = () => {
    setDetailsPanelOpen(false);
    setSelectedAccount(null);
  };

  const exportToCSV = () => {
    const headers = [
      'Account Name', 'Account ID', 'Email', 'Status', 'OU Name', 
      'Joined Method', 'Joined Date', 'Functional Unit', 'Business Unit', 
      'Account Owner', 'Technical Contact'
    ];
    
    const csvData = filteredAccounts.map(account => [
      account.name,
      account.id,
      account.email,
      account.status,
      account.ouName || '',
      account.joinedMethod || '',
      account.joinedTimestamp ? new Date(account.joinedTimestamp).toLocaleDateString() : '',
      account.tags?.functionalUnit || '',
      account.tags?.businessUnit || '',
      account.tags?.accountOwner || '',
      account.tags?.technicalContact || ''
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `verisk-aws-accounts-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



  const [functionalUnitFilter, setFunctionalUnitFilter] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState('');
  const [accountOwnerFilter, setAccountOwnerFilter] = useState('');
  const [technicalContactFilter, setTechnicalContactFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [clearCacheDialogOpen, setClearCacheDialogOpen] = useState(false);
  const [appDrawerOpen, setAppDrawerOpen] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState('');

  const columns = useMemo(() => {
    const baseColumns = [
      { field: 'name', headerName: 'Account Name', flex: 1, minWidth: 150, sortable: true },
      { field: 'id', headerName: 'Account ID', width: 120, sortable: true },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 180, sortable: true },
      { 
        field: 'status', 
        headerName: 'Status', 
        width: 100, 
        sortable: true,
        renderCell: (params) => (
          <Chip 
            label={params.value || 'Unknown'}
            color={params.value === 'ACTIVE' ? 'success' : 'error'}
            size="small"
          />
        )
      },
      { field: 'ouName', headerName: 'OU Name', width: 120, sortable: true },
      { field: 'joinedMethod', headerName: 'Joined Method', width: 100, sortable: true },
      { 
        field: 'joinedTimestamp', 
        headerName: 'Joined Date', 
        width: 120, 
        sortable: true,
        renderCell: (params) => {
          try {
            if (!params.value || params.value === '') return <span>-</span>;
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return <span>Invalid Date</span>;
            return <span>{date.toLocaleDateString()}</span>;
          } catch (error) {
            console.warn('Error formatting date:', error, params.value);
            return <span>Error</span>;
          }
        }
      },
    ];

    // Show tag columns when filters are applied
    if (functionalUnitFilter && functionalUnitFilter.trim() !== '') {
      baseColumns.push({
        field: 'functionalUnit',
        headerName: 'Functional Unit',
        width: 130,
        sortable: true,
        renderCell: (params) => {
          const functionalUnit = params.row?.tags?.functionalUnit || 'N/A';
          return <span>{functionalUnit}</span>;
        }
      });
    }
    
    if (businessUnitFilter && businessUnitFilter.trim() !== '') {
      baseColumns.push({
        field: 'businessUnit',
        headerName: 'Business Unit',
        width: 130,
        sortable: true,
        renderCell: (params) => {
          const businessUnit = params.row?.tags?.businessUnit || 'N/A';
          return <span>{businessUnit}</span>;
        }
      });
    }
    
    if (accountOwnerFilter && accountOwnerFilter.trim() !== '') {
      baseColumns.push({
        field: 'accountOwner',
        headerName: 'Account Owner',
        width: 150,
        sortable: true,
        renderCell: (params) => {
          const accountOwner = params.row?.tags?.accountOwner || 'N/A';
          return <span>{accountOwner}</span>;
        }
      });
    }
    
    if (technicalContactFilter && technicalContactFilter.trim() !== '') {
      baseColumns.push({
        field: 'technicalContact',
        headerName: 'Technical Contact',
        width: 150,
        sortable: true,
        renderCell: (params) => {
          const technicalContact = params.row?.tags?.technicalContact || 'N/A';
          return <span>{technicalContact}</span>;
        }
      });
    }

    baseColumns.push(
      {
        field: 'actions',
        headerName: 'View',
        width: 80,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(params.row);
            }}
          >
            <ExpandMore />
          </IconButton>
        )
      }
    );

    return baseColumns;
  }, [functionalUnitFilter, businessUnitFilter, accountOwnerFilter, technicalContactFilter]);

  const logout = () => oktaAuth.signOut();

  const authenticatedFetch = async (url, options = {}) => {
    const headers = { 'Content-Type': 'application/json' };
    
    if (authState?.accessToken?.accessToken) {
      headers['Authorization'] = `Bearer ${authState.accessToken.accessToken}`;
    }
    
    return fetch(url, { headers, ...options });
  };

  const loadAccountCounts = async () => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINT}/accounts?counts-only=true`);
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      
      if (result.counts) {
        setAccountCounts(result.counts);
      } else {
        // Fallback: if API doesn't support counts-only, load all and extract counts
        const accounts = result;
        setAccountCounts({
          total: accounts.length,
          active: accounts.filter(acc => acc.status === 'ACTIVE').length,
          suspended: accounts.filter(acc => acc.status === 'SUSPENDED').length
        });
      }
    } catch (err) {
      console.error('Error loading counts:', err.message);
    }
  };

  const loadAccounts = async (forceReload = false) => {
    if (accountsLoaded && !forceReload) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINT}/accounts`);
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      
      // Handle both old and new response formats
      const accounts = Array.isArray(result) ? result : (result.accounts || []);
      
      console.log('Loaded accounts:', accounts.slice(0, 2)); // Debug: show first 2 accounts
      console.log('Sample account tags:', accounts[0]?.tags); // Debug: show tag structure
      setAllAccounts(accounts);
      setAccountsLoaded(true);
      
      // Update counts from actual data
      setAccountCounts({
        total: accounts.length,
        active: accounts.filter(acc => acc.status === 'ACTIVE').length,
        suspended: accounts.filter(acc => acc.status === 'SUSPENDED').length
      });
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError(`Failed to load accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadOUs = async () => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINT}/ous`);
      const ous = await response.json();
      if (ous.error) throw new Error(ous.error);
      setAllOUs(ous);
    } catch (err) {
      console.error(`Error loading OUs: ${err.message}`);
    }
  };

  const loadAccountsForOU = async (ouId) => {
    if (!ouId) {
      await loadAccounts();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINT}/accounts?ou-id=${ouId}`);
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      
      // Handle both old and new response formats
      const accounts = Array.isArray(result) ? result : (result.accounts || []);
      
      setAllAccounts(accounts);
      setFilteredAccounts(accounts);
      setCurrentOU(allOUs.find(ou => ou.id === ouId));
      setAccountsLoaded(true);
    } catch (err) {
      console.error('Error loading accounts for OU:', err);
      setError(`Failed to load accounts for OU: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const wildcardMatch = (text, pattern) => {
    if (!pattern || pattern.trim() === '') return true;
    if (!text || text === '') return false;
    
    const lowerText = text.toLowerCase().trim();
    const lowerPattern = pattern.toLowerCase().trim();
    
    if (lowerPattern.startsWith('*') && lowerPattern.endsWith('*')) {
      const searchTerm = lowerPattern.slice(1, -1);
      return searchTerm === '' || lowerText.includes(searchTerm);
    } else if (lowerPattern.startsWith('*')) {
      const searchTerm = lowerPattern.slice(1);
      return searchTerm === '' || lowerText.endsWith(searchTerm);
    } else if (lowerPattern.endsWith('*')) {
      const searchTerm = lowerPattern.slice(0, -1);
      return searchTerm === '' || lowerText.startsWith(searchTerm);
    }
    return lowerText.includes(lowerPattern);
  };

  const filterAccounts = () => {
    try {
      let filtered = [...allAccounts];
      
      if (searchTerm) {
        filtered = filtered.filter(account => {
          const name = account.name || '';
          const id = account.id || '';
          const email = account.email || '';
          return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 id.includes(searchTerm) ||
                 email.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }
      
      if (functionalUnitFilter && functionalUnitFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.functionalUnit || '', functionalUnitFilter)
        );
      }
      
      if (businessUnitFilter && businessUnitFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.businessUnit || '', businessUnitFilter)
        );
      }
      
      if (accountOwnerFilter && accountOwnerFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.accountOwner || '', accountOwnerFilter)
        );
      }
      
      if (technicalContactFilter && technicalContactFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.technicalContact || '', technicalContactFilter)
        );
      }
      
      if (statusFilter) {
        filtered = filtered.filter(account => account.status === statusFilter);
      }
      
      setFilteredAccounts(filtered);
    } catch (error) {
      console.error('Error filtering accounts:', error);
      setError('Error filtering accounts. Please try again.');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedOU('');
    setCurrentOU(null);
    setSelectedAccount(null);
    setFunctionalUnitFilter('');
    setBusinessUnitFilter('');
    setAccountOwnerFilter('');
    setTechnicalContactFilter('');
    setStatusFilter('');
    setAccountsLoaded(false);
    setAllAccounts([]);
    setFilteredAccounts([]);
    loadAccountCounts();
  };

  const clearCache = async () => {
    setClearCacheDialogOpen(false);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINT}/clear-cache`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      setAccountsLoaded(false);
      setAllAccounts([]);
      setFilteredAccounts([]);
      loadAccountCounts();
    } catch (err) {
      setError(`Error clearing cache: ${err.message}`);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopySnackbarMessage(`${label} copied to clipboard`);
        setCopySnackbarOpen(true);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        setCopySnackbarMessage(`${label} copied to clipboard`);
        setCopySnackbarOpen(true);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setCopySnackbarMessage(`Failed to copy ${label}`);
      setCopySnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (authState?.isAuthenticated) {
      loadAccountCounts(); // Only load counts initially
      loadOUs(); // Auto-load OUs
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    const hasFilters = searchTerm || functionalUnitFilter || businessUnitFilter || accountOwnerFilter || technicalContactFilter;
    if (hasFilters && !accountsLoaded) {
      loadAccounts();
    }
  }, [searchTerm, functionalUnitFilter, businessUnitFilter, accountOwnerFilter, technicalContactFilter]);

  useEffect(() => {
    if (accountsLoaded) {
      filterAccounts();
    }
  }, [allAccounts, searchTerm, functionalUnitFilter, businessUnitFilter, accountOwnerFilter, technicalContactFilter, statusFilter, accountsLoaded]);

  const handleCardClick = async (cardType) => {
    if (!accountsLoaded) {
      await loadAccounts();
    }
    
    if (statusFilter === cardType) {
      // Toggle off
      setStatusFilter('');
    } else {
      // Set new filter
      setStatusFilter(cardType);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
              <img 
                src="/verisk_logo_white.svg" 
                alt="Verisk Logo" 
                style={{ height: '32px', marginRight: '12px' }}
              />
              <Box sx={{ borderLeft: '1px solid rgba(255, 255, 255, 0.3)', height: '24px', mr: 1 }} />
              <Typography 
                variant="h6" 
                noWrap 
                component="div"
                sx={{ fontWeight: 400, letterSpacing: '0.5px' }}
              >
                Verisk Cloud Portal
              </Typography>
              {API_ENDPOINT.includes('vnhzzdkdyl') && (
                <Chip 
                  label="TEST" 
                  size="small" 
                  color="warning" 
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>
            <TextField
              size="small"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              sx={{
                width: 400,
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  opacity: 1,
                },
              }}
            />
            <Button
              color="inherit"
              startIcon={<Apps />}
              onClick={() => setAppDrawerOpen(true)}
              sx={{ ml: 2, mr: 2, textTransform: 'none' }}
            >
              Services
            </Button>
            <Tooltip title={!accountsLoaded || filteredAccounts.length === 0 ? "Load accounts to enable export" : "Export filtered accounts to CSV"}>
              <span>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FileDownload />}
                  onClick={exportToCSV}
                  disabled={!accountsLoaded || filteredAccounts.length === 0}
                  sx={{ mr: 2 }}
                  size="small"
                >
                  Export CSV
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Clear cache (Admin)">
              <IconButton color="inherit" onClick={() => setClearCacheDialogOpen(true)} sx={{ mr: 1 }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {authState?.idToken?.claims?.name || authState?.idToken?.claims?.preferred_username || authState?.idToken?.claims?.email}
            </Typography>
            <IconButton color="inherit" onClick={logout}>
              <Logout />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList /> Filters
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={allOUs}
                getOptionLabel={(option) => `${option.name} (${option.type})`}
                value={allOUs.find(ou => ou.id === selectedOU) || null}
                onChange={(event, newValue) => {
                  const ouId = newValue ? newValue.id : '';
                  setSelectedOU(ouId);
                  loadAccountsForOU(ouId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organizational Unit"
                    placeholder="Select OU..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <AccountTree sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                    InputLabelProps={{
                      sx: { fontWeight: 500 }
                    }}
                  />
                )}
              />
            </Box>
            
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Functional Unit"
                placeholder="e.g., EES* or UW"
                value={functionalUnitFilter}
                onChange={(e) => setFunctionalUnitFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., EES*, *UW*)"
              />
            </Box>
            
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Business Unit"
                placeholder="e.g., CDO, Claims"
                value={businessUnitFilter}
                onChange={(e) => setBusinessUnitFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., *Claims, CDO*)"
              />
            </Box>
            
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Account Owner"
                placeholder="e.g., John*"
                value={accountOwnerFilter}
                onChange={(e) => setAccountOwnerFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., John*, *Smith)"
              />
            </Box>
            
            <Box sx={{ mb: 2.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Technical Contact"
                placeholder="e.g., *@verisk.com"
                value={technicalContactFilter}
                onChange={(e) => setTechnicalContactFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., *@verisk.com)"
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<Close />}
              onClick={clearSearch}
              size="small"
              fullWidth
              disabled={!searchTerm && !selectedOU && !functionalUnitFilter && !businessUnitFilter && !accountOwnerFilter && !technicalContactFilter && !statusFilter}
              sx={{
                bgcolor: 'grey.700',
                '&:hover': { bgcolor: 'grey.800' },
                '&.Mui-disabled': { bgcolor: 'grey.300' }
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        </Drawer>
        
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { boxShadow: 4 },
                  border: statusFilter === '' && accountsLoaded ? 2 : 0,
                  borderColor: 'primary.main'
                }}
                onClick={() => handleCardClick('')}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Accounts{currentOU ? ` (${currentOU.name})` : ''}
                  </Typography>
                  <Typography variant="h4" component="div">
                    {accountsLoaded ? filteredAccounts.length : accountCounts.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { boxShadow: 4 },
                  border: statusFilter === 'ACTIVE' ? 2 : 0,
                  borderColor: 'success.main'
                }}
                onClick={() => handleCardClick('ACTIVE')}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Accounts{currentOU ? ` (${currentOU.name})` : ''}
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {accountsLoaded ? filteredAccounts.filter(acc => acc.status === 'ACTIVE').length : accountCounts.active}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { boxShadow: 4 },
                  border: statusFilter === 'SUSPENDED' ? 2 : 0,
                  borderColor: 'error.main'
                }}
                onClick={() => handleCardClick('SUSPENDED')}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Suspended Accounts{currentOU ? ` (${currentOU.name})` : ''}
                  </Typography>
                  <Typography variant="h4" component="div" color="error.main">
                    {accountsLoaded ? filteredAccounts.filter(acc => acc.status === 'SUSPENDED').length : accountCounts.suspended}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: {error}
            </Alert>
          )}
          
          {!loading && !error && !accountsLoaded && (
            <Alert severity="info" sx={{ textAlign: 'center' }}>
              Click a card above or apply filters to view accounts
            </Alert>
          )}
          
          {!loading && !error && accountsLoaded && filteredAccounts.length === 0 && (
            <Alert severity="info">
              No accounts found matching your search.
            </Alert>
          )}
          
          {!loading && !error && accountsLoaded && filteredAccounts.length > 0 && (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" color="primary">
                  Accounts ({filteredAccounts.length} results)
                </Typography>
              </Box>
              <ErrorBoundary>
                <Box sx={{ height: 600, width: '100%' }}>
                  <DataGrid
                    rows={filteredAccounts}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    disableSelectionOnClick
                    density="compact"
                    onRowClick={(params) => handleRowClick(params.row)}
                    onError={(error) => {
                      console.error('DataGrid error:', error);
                      setError('Error displaying data. Please try again.');
                    }}
                    sx={{
                      '& .MuiDataGrid-cell:hover': {
                        color: 'primary.main',
                      },
                      '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                      },
                    }}
                  />
                </Box>
              </ErrorBoundary>
            </Box>
          )}
          

        </Box>
        
        {/* Side Panel Drawer */}
        <Drawer
          anchor="right"
          open={detailsPanelOpen}
          onClose={closeDetailsPanel}
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              p: 0,
            },
          }}
        >
          {selectedAccount && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" color="primary">
                  Account Details
                </Typography>
                <IconButton onClick={closeDetailsPanel}>
                  <Close />
                </IconButton>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedAccount.name}
                </Typography>
                <Chip 
                  label={selectedAccount.status}
                  color={selectedAccount.status === 'ACTIVE' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Basic Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      <strong>Account ID:</strong> {selectedAccount.id}
                    </Typography>
                    <Tooltip title="Copy Account ID">
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(selectedAccount.id, 'Account ID')}
                        sx={{ ml: 1 }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      <strong>Email:</strong> {selectedAccount.email}
                    </Typography>
                    <Tooltip title="Copy Email">
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(selectedAccount.email, 'Email')}
                        sx={{ ml: 1 }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>OU Name:</strong> {selectedAccount.ouName || 'N/A'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Account History
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Joined Method:</strong> {selectedAccount.joinedMethod || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Joined Date:</strong> {selectedAccount.joinedTimestamp ? new Date(selectedAccount.joinedTimestamp).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
                
                {selectedAccount.tags && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Account Tags
                    </Typography>
                    {selectedAccount.tags.functionalUnit && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Functional Unit:</strong> {selectedAccount.tags.functionalUnit}
                      </Typography>
                    )}
                    {selectedAccount.tags.businessUnit && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Business Unit:</strong> {selectedAccount.tags.businessUnit}
                      </Typography>
                    )}
                    {selectedAccount.tags.accountOwner && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Account Owner:</strong> {selectedAccount.tags.accountOwner}
                      </Typography>
                    )}
                    {selectedAccount.tags.technicalContact && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Technical Contact:</strong> {selectedAccount.tags.technicalContact}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {selectedAccount.approvers && selectedAccount.approvers.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Account Approvers
                    </Typography>
                    {selectedAccount.approvers.map((approver, index) => (
                      <Typography variant="body2" sx={{ mb: 1 }} key={index}>
                        <strong>{approver.name || approver}</strong>
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Drawer>
        
        {/* App Drawer for Services */}
        <Drawer
          anchor="right"
          open={appDrawerOpen}
          onClose={() => setAppDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              p: 3,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" color="primary">
              Cloud Services
            </Typography>
            <IconButton onClick={() => setAppDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: 2,
                  borderColor: 'primary.main',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalance sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Account Explorer</Typography>
                      <Chip label="Current" size="small" color="primary" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    View and manage AWS Organizations accounts with advanced filtering
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => { setAppDrawerOpen(false); alert('Account Factory - Coming Soon'); }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloudQueue sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Account Factory</Typography>
                      <Chip label="Coming Soon" size="small" color="warning" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Automated provisioning of new AWS accounts with standardized configurations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => { setAppDrawerOpen(false); alert('Cloud Governance - Coming Soon'); }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Security sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Cloud Governance</Typography>
                      <Chip label="Coming Soon" size="small" color="warning" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Enforce policies, compliance rules, and security standards across accounts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => { setAppDrawerOpen(false); alert('Cost Analytics - Coming Soon'); }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Assessment sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Cost Analytics</Typography>
                      <Chip label="Coming Soon" size="small" color="warning" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Track spending, analyze costs, and optimize cloud resource usage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Drawer>
        
        {/* Clear Cache Confirmation Dialog */}
        <Dialog
          open={clearCacheDialogOpen}
          onClose={() => setClearCacheDialogOpen(false)}
        >
          <DialogTitle>Clear Cache?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will clear all cached account and OU data. The data will be refreshed from AWS Organizations on the next load.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearCacheDialogOpen(false)}>Cancel</Button>
            <Button onClick={clearCache} color="error" variant="contained">
              Clear Cache
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Copy Feedback Snackbar */}
        <Snackbar
          open={copySnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setCopySnackbarOpen(false)}
          message={copySnackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
      
      {/* Version Footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          p: 1,
          bgcolor: 'rgba(0, 0, 0, 0.05)',
          borderTopLeftRadius: 8,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          v1.0.0
        </Typography>
      </Box>
    </ThemeProvider>
  );
};

function App() {
  const { authState, oktaAuth } = useOktaAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking authentication state...');
        if (oktaAuth.isLoginRedirect()) {
          console.log('Handling login redirect...');
          await oktaAuth.handleLoginRedirect();
        }
        const isAuthenticated = await oktaAuth.isAuthenticated();
        console.log('Is authenticated:', isAuthenticated);
        setLoading(false);
      } catch (error) {
        console.error('Auth check error:', error);
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [oktaAuth]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (authState?.error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Alert severity="error">
            <Typography variant="h6">Authentication Error</Typography>
            <Typography>{authState.error.message}</Typography>
            <Button onClick={() => oktaAuth.signInWithRedirect()} sx={{ mt: 2 }}>
              Try Again
            </Button>
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  console.log('App authState:', authState);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/*" element={authState?.isAuthenticated ? <Dashboard /> : <Login />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;